import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, ProgressBar, Alert, Button } from "react-bootstrap";
import PermissionWrapper from "../components/PermissionWrapper";
import PackageService, { GroupCredits } from "../services/PackageService";
import { useSelector } from "react-redux";

const PackageStatsPage: React.FC = () => {
  const [groupCredits, setGroupCredits] = useState<GroupCredits | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');

  const { user } = useSelector((state: any) => state.auth);

  useEffect(() => {
    // Get user's first group ID (assuming user belongs to groups)
    if (user && user.groups && user.groups.length > 0) {
      setSelectedGroupId(user.groups[0]);
      fetchGroupCredits(user.groups[0]);
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchGroupCredits = async (groupId: string) => {
    try {
      setLoading(true);
      const data = await PackageService.getGroupCredits(groupId);
      setGroupCredits(data);
    } catch (error) {
      console.error('Error fetching group credits:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculatePercentage = (used: number, total: number) => {
    if (total === 0) return 0;
    return Math.round((used / total) * 100);
  };

  const getProgressVariant = (percentage: number) => {
    if (percentage >= 90) return 'danger';
    if (percentage >= 70) return 'warning';
    return 'success';
  };

  if (loading) {
    return (
      <Container>
        <div className="text-center mt-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </Container>
    );
  }

  if (!groupCredits || !groupCredits.hasActivePackage) {
    return (
      <PermissionWrapper pageName="PackageStatsPage">
        <Container fluid>
          <Row className="mb-4">
            <Col>
              <h2>
                <i className="bi bi-graph-up me-2"></i>
                Package Statistics
              </h2>
            </Col>
          </Row>
          
          <Row>
            <Col>
              <Alert variant="warning" className="text-center">
                <i className="bi bi-exclamation-triangle me-2"></i>
                No active package found for your group. Please purchase a package to start using the system.
                <div className="mt-3">
                  <Button variant="primary" href="/packages">
                    Browse Packages
                  </Button>
                </div>
              </Alert>
            </Col>
          </Row>
        </Container>
      </PermissionWrapper>
    );
  }

  const schedulePercentage = calculatePercentage(
    groupCredits.usedCredits.scheduleCredits, 
    groupCredits.totalCredits.scheduleCredits
  );
  
  const ticketPercentage = calculatePercentage(
    groupCredits.usedCredits.ticketCredits, 
    groupCredits.totalCredits.ticketCredits
  );

  return (
    <PermissionWrapper pageName="PackageStatsPage">
      <Container fluid>
        <Row className="mb-4">
          <Col>
            <h2>
              <i className="bi bi-graph-up me-2"></i>
              Package Statistics
            </h2>
            <p className="text-muted">Monitor your package usage and remaining credits</p>
          </Col>
        </Row>

        {/* Package Info */}
        <Row className="mb-4">
          <Col>
            <Card className="border-primary">
              <Card.Header className="bg-primary text-white">
                <h5 className="mb-0">
                  <i className="bi bi-box-seam me-2"></i>
                  Active Package: {groupCredits.packageName}
                </h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <div className="text-center">
                      <h3 className="text-success">{groupCredits.remainingCredits.scheduleCredits}</h3>
                      <p className="text-muted mb-0">Schedules Remaining</p>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="text-center">
                      <h3 className="text-success">{groupCredits.remainingCredits.ticketCredits}</h3>
                      <p className="text-muted mb-0">Tickets Remaining</p>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Usage Statistics */}
        <Row className="g-4">
          {/* Schedule Usage */}
          <Col md={6}>
            <Card className="h-100">
              <Card.Header>
                <h5>
                  <i className="bi bi-calendar2-week me-2"></i>
                  Schedule Usage
                </h5>
              </Card.Header>
              <Card.Body>
                <div className="mb-3">
                  <div className="d-flex justify-content-between mb-2">
                    <span>Used: {groupCredits.usedCredits.scheduleCredits}</span>
                    <span>Total: {groupCredits.totalCredits.scheduleCredits}</span>
                  </div>
                  <ProgressBar 
                    now={schedulePercentage} 
                    variant={getProgressVariant(schedulePercentage)}
                    label={`${schedulePercentage}%`}
                  />
                </div>
                
                <div className="row text-center">
                  <div className="col">
                    <div className="border rounded p-2">
                      <h6 className="text-primary mb-1">{groupCredits.usedCredits.scheduleCredits}</h6>
                      <small className="text-muted">Used</small>
                    </div>
                  </div>
                  <div className="col">
                    <div className="border rounded p-2">
                      <h6 className="text-success mb-1">{groupCredits.remainingCredits.scheduleCredits}</h6>
                      <small className="text-muted">Remaining</small>
                    </div>
                  </div>
                  <div className="col">
                    <div className="border rounded p-2">
                      <h6 className="text-info mb-1">{groupCredits.totalCredits.scheduleCredits}</h6>
                      <small className="text-muted">Total</small>
                    </div>
                  </div>
                </div>

                {schedulePercentage >= 90 && (
                  <Alert variant="danger" className="mt-3 mb-0">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    Schedule credits are running low!
                  </Alert>
                )}
                {schedulePercentage >= 70 && schedulePercentage < 90 && (
                  <Alert variant="warning" className="mt-3 mb-0">
                    <i className="bi bi-info-circle me-2"></i>
                    Consider purchasing additional credits soon.
                  </Alert>
                )}
              </Card.Body>
            </Card>
          </Col>

          {/* Ticket Usage */}
          <Col md={6}>
            <Card className="h-100">
              <Card.Header>
                <h5>
                  <i className="bi bi-ticket-perforated me-2"></i>
                  Ticket Usage
                </h5>
              </Card.Header>
              <Card.Body>
                <div className="mb-3">
                  <div className="d-flex justify-content-between mb-2">
                    <span>Used: {groupCredits.usedCredits.ticketCredits}</span>
                    <span>Total: {groupCredits.totalCredits.ticketCredits}</span>
                  </div>
                  <ProgressBar 
                    now={ticketPercentage} 
                    variant={getProgressVariant(ticketPercentage)}
                    label={`${ticketPercentage}%`}
                  />
                </div>
                
                <div className="row text-center">
                  <div className="col">
                    <div className="border rounded p-2">
                      <h6 className="text-primary mb-1">{groupCredits.usedCredits.ticketCredits}</h6>
                      <small className="text-muted">Used</small>
                    </div>
                  </div>
                  <div className="col">
                    <div className="border rounded p-2">
                      <h6 className="text-success mb-1">{groupCredits.remainingCredits.ticketCredits}</h6>
                      <small className="text-muted">Remaining</small>
                    </div>
                  </div>
                  <div className="col">
                    <div className="border rounded p-2">
                      <h6 className="text-info mb-1">{groupCredits.totalCredits.ticketCredits}</h6>
                      <small className="text-muted">Total</small>
                    </div>
                  </div>
                </div>

                {ticketPercentage >= 90 && (
                  <Alert variant="danger" className="mt-3 mb-0">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    Ticket credits are running low!
                  </Alert>
                )}
                {ticketPercentage >= 70 && ticketPercentage < 90 && (
                  <Alert variant="warning" className="mt-3 mb-0">
                    <i className="bi bi-info-circle me-2"></i>
                    Consider purchasing additional credits soon.
                  </Alert>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Action Buttons */}
        <Row className="mt-4">
          <Col className="text-center">
            {(schedulePercentage >= 70 || ticketPercentage >= 70) && (
              <div className="d-flex gap-2 justify-content-center">
                <Button variant="primary" href="/packages">
                  <i className="bi bi-plus-circle me-2"></i>
                  Purchase More Credits
                </Button>
                <Button variant="outline-primary" onClick={() => fetchGroupCredits(selectedGroupId)}>
                  <i className="bi bi-arrow-clockwise me-2"></i>
                  Refresh Stats
                </Button>
              </div>
            )}
          </Col>
        </Row>

        {/* Usage Tips */}
        <Row className="mt-4">
          <Col>
            <Card className="bg-light">
              <Card.Body>
                <h6>
                  <i className="bi bi-lightbulb me-2"></i>
                  Usage Tips
                </h6>
                <ul className="mb-0">
                  <li>Each schedule you create consumes 1 schedule credit</li>
                  <li>Each ticket option you add to a schedule consumes ticket credits based on seat count</li>
                  <li>Monitor your usage regularly to avoid service interruption</li>
                  <li>Purchase additional packages when credits are running low</li>
                </ul>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </PermissionWrapper>
  );
};

export default PackageStatsPage;